import React, { useEffect, useRef, useState } from "react";
// nodejs library that concatenates classes
import classNames from "classnames";

import { useTranslation } from 'react-i18next';
import { useMutation } from "@apollo/react-hooks";
import gql from "graphql-tag";
import sanitizeHtml from "sanitize-html";

// @material-ui/core components
import { makeStyles } from "@material-ui/core/styles";
import { Hidden } from "@material-ui/core";

// @material-ui/icons
// core components
import Header from "components/Header/Header.js";
import Footer from "components/Footer/Footer.js";
import GridContainer from "components/Grid/GridContainer.js";
import GridItem from "components/Grid/GridItem.js";
import Button from "components/CustomButtons/Button.js";
import Parallax from "components/Parallax/Parallax.js";
import CustomInput from "components/CustomInput/CustomInput.js";
// sections for this page
import HeaderLinks from "components/Header/HeaderLinks.js";

import styles from "assets/jss/material-kit-react/views/components.js";

import diamondOneImage from "assets/img/diamond-1-company.png";
import diamondTwoImage from "assets/img/diamond-2-company.png";
import diamondsImage from "assets/img/diamonds.png";
import checkMark from "assets/img/check.png";
import phoneImage from "assets/img/phone-company.png";
import fillIcon from "assets/img/fill.png";
import candidatesIcon from "assets/img/profile.png";
import handshakeIcon from "assets/img/handshake.png";
import coinsIcon from "assets/img/coins.png";
import stallImage from "assets/img/stall.png";

const useStyles = makeStyles(styles);

const ADD_MAIL = gql`
  mutation addUserMailingList($email:String!, $lang:String! $type:String!, $validationToken:String!){
    addUserMailingList(email:$email, lang:$lang, type:$type, validationToken: $validationToken)
}`;

const scrollToRef = ref => {
  if (window.innerWidth < 960) {
    window.scrollTo({
      left: 0,
      top: ref.current.getBoundingClientRect().top + window.scrollY - 80,
      behavior: 'smooth'
    });
  }
  else {
    window.scrollTo({
      left: 0,
      top: ref.current.getBoundingClientRect().top + window.scrollY - 480,
      behavior: 'smooth'
    });
  }
}

const scrollToView = ref => {
  ref.current.scrollIntoView({ behavior: 'smooth' });
}

const CompanyPage = props => {
  const { t, i18n } = useTranslation();
  const classes = useStyles();
  const { ...rest } = props;

  const moreInfoSection = useRef(null);
  const howItWorksSection = useRef(null);
  const signupSection = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [inputValue, setInputValue] = useState('');
  const [displayError, setDisplayError] = useState(false);
  const [messageSent, setMessageSent] = useState(false);

  const validEmail = (mail) => {
    return (/^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/.test(mail));
  }

  const handleChange = event => {
    setInputValue(event.target.value);
    setDisplayError(!validEmail(event.target.value));
  }

  const [addMail] = useMutation(ADD_MAIL)

  const handleClick = event => {
    if (inputValue && validEmail(inputValue)) {
      window.grecaptcha.execute('6LfvvegUAAAAAHLInYv7LJU63Ypk0iFEXMvDaa0s', { action: 'registration' }).then(function (token) {
        addMail({ variables: { 'email': inputValue, 'lang': i18n.language, 'type': 'company', 'validationToken': token } });
      });
      setMessageSent(true);
    } else {
      setDisplayError(true);
    }
  };

  return (
    <div>
      <Header
        fixed
        color="transparent"
        changeColorOnScroll={{
          height: 50,
          color: "primary"
        }}
        {...rest}
      />
      <Parallax>
        <GridContainer justify="center" className={classNames(classes.conatinerFluid, classes.absolute)}>
          <GridItem xs={12} md={10} className={classes.parallaxContent}>
            <img className={classes.stallImage} src={stallImage} />
            <div className={classes.parallaxInfoContainer}>
              <p className={classes.subtitle}>
                {t('company.header.subtitle')}
              </p>
            </div>
            <Hidden smDown>
              <div className={classes.signupContainer}>
              {!messageSent
                ?<>
                  <p className={classes.signupParagraph}>
                    {t('company.signup.text')}
                  </p>
                  <CustomInput
                    id="regular"
                    labelText={t('company.signup.email.placeholder')}
                    inputProps={{
                      value: inputValue,
                      onChange: handleChange
                    }}
                    success={validEmail(inputValue)}
                    error={displayError}
                    formControlProps={{
                      fullWidth: true
                    }}
                  />
                  <div className={classes.signupButtonsContainer}>
                    <Button
                      onClick={handleClick}
                      fullWidth={true}
                      className={classes.formButton}
                      color="black"
                    >
                      {t('company.signup.buttons.send')}
                    </Button>
                    <Button
                      fullWidth={true}
                      className={classes.formButton}
                      color="white"
                      onClick={() => scrollToRef(moreInfoSection)}
                    >
                      {t('company.signup.buttons.more-info')}
                    </Button>
                  </div>
                </>
                : <p className={classNames(classes.headline, classes.signupConfirmation)}>
                  {t('company.signup.sent')}
                </p>
                }
              </div>
            </Hidden>
            <div className={classes.decorationCircle}></div>
            <div className={classes.decorationLineHeader}></div>
          </GridItem>
        </GridContainer>
      </Parallax>

      <div className={classNames(classes.main, classes.whiteBackground)}>
        <Hidden mdUp>
          <GridContainer justify="center">
            <GridItem xs={12} md={10} className={classes.section}>
              <div className={classes.decorationLineSectionOne} />
              <div className={classes.decorationLineHorizontalOne} />
              <div className={classes.decorationCircleHorizontal} />
              <div>
                {!messageSent
                  ?<>
                  <p className={classes.signupParagraph}>
                    {t('company.signup.text')}
                  </p>
                  <CustomInput
                    id="regular"
                    labelText={t('company.signup.email.placeholder')}
                    inputProps={{
                      value: inputValue,
                      onChange: handleChange
                    }}
                    success={validEmail(inputValue)}
                    error={displayError}
                    formControlProps={{
                      fullWidth: true
                    }}
                  />
                  <div>
                    <Button
                      fullWidth={true}
                      className={classes.formButton}
                      onClick={handleClick}
                      color="black"
                    >
                      {t('company.signup.buttons.send')}
                    </Button>
                    <Button
                      fullWidth={true}
                      className={classes.formButton}
                      color="white"
                      onClick={() => scrollToRef(moreInfoSection)}
                    >
                      {t('company.signup.buttons.more-info')}
                    </Button>
                  </div>
                </>
                : <p className={classNames(classes.headline, classes.signupConfirmation)}>
                  {t('company.signup.sent')}
                </p>
                }
              </div>
            </GridItem>
          </GridContainer>
        </Hidden>
        <GridContainer>
          <GridItem xs={12} md={10} className={classNames(classes.section, classes.noBottomPadding)}>
            <div className={classes.decorationLineSectionTwo} />
            <div className={classes.decorationLineHorizontalTwo} />
            <Hidden smDown>
              <div className={classes.decorationLineVertical} />
              <div className={classes.decorationCircleVertical} />
            </Hidden>
            <div className={classes.sectionOneHeading} ref={moreInfoSection}>
              <p className={classes.headline}>
                {t('company.advantages.title')}
              </p>
            </div>
            <div className={classes.diamondOneContainer}>
              <img src={diamondOneImage} className={classes.diamondImage} />
            </div>
            <div className={classes.sectionOneContainer}>
              <p className={classes.sectionOneParagraph}>
                {t('company.advantages.subtitle')}
              </p>
            </div>
            <div className={classes.checkMarksContainer}>
              <div className={classes.checkMarkContainer}>
                <img src={checkMark} alt="check mark" className={classes.checkMark} />
                <p className={classes.checkMarkText}>
                  {t('company.advantages.list.time')}
                </p>
              </div>
              <div className={classes.checkMarkContainer}>
                <img src={checkMark} alt="check mark" className={classes.checkMark} />
                <p className={classes.checkMarkText}>
                  {t('company.advantages.list.cost')}
                </p>
              </div>
              <div className={classes.checkMarkContainer}>
                <img src={checkMark} alt="check mark" className={classes.checkMark} />
                <p className={classes.checkMarkText}>
                  {t('company.advantages.list.quality')}
                </p>
              </div>
              <div className={classes.checkMarkContainer}>
                <img src={checkMark} alt="check mark" className={classes.checkMark} />
                <p className={classes.checkMarkText}>
                  {t('company.advantages.list.anywhere')}
                </p>
              </div>
              <div className={classes.signupButtonsContainer}>
                <Button
                  fullWidth={true}
                  className={classes.formButton}
                  color="primary"
                  onClick={() => scrollToView(howItWorksSection)}
                >
                  {t('company.advantages.signup.buttons.how-it-works')}
                </Button>
              </div>
            </div>
          </GridItem>
        </GridContainer>
        <GridContainer>
          <GridItem xs={12} md={10} className={classNames(classes.section, classes.noTopPadding)}>
            <div className={classes.decorationLineSectionThree} />
            <div className={classes.decorationCircleHorizontalTwo} />
            <div className={classes.decorationLineSectionFour} />
            <div className={classes.diamondsContainer}>
              <img src={diamondsImage} className={classes.diamondImage} />
            </div>
            <div className={classes.imageAndMottoContainer} ref={howItWorksSection}>
              <img src={phoneImage} className={classes.phoneImage} />
              <div className={classes.mottoContainer}>
                <p className={classes.mottoText}>
                  {t('fairhub.motto')}
                </p>
                <div
                  className={classes.paragraph}
                  dangerouslySetInnerHTML={{__html: sanitizeHtml(t('company.how-it-works.concept'))}}
                />
                <Hidden smDown>
                  <div className={classes.signupButtonsContainer}>
                    <Button
                      color="primary"
                      onClick={() => scrollToView(signupSection)}
                    >
                      {t('company.advantages.signup.buttons.register')}
                    </Button>
                  </div>
                </Hidden>
              </div>
            </div>
            <div className={classes.iconsContainer}>
              <div className={classes.iconContainer}>
                <img src={fillIcon} alt={t('company.how-it-works.job')} className={classes.icon} />
                <p className={classes.iconText}>
                  {t('company.how-it-works.job')}
                </p>
              </div>
              <div className={classes.iconContainer}>
                <img src={candidatesIcon} alt={t('company.how-it-works.candicates')} className={classes.icon} />
                <p className={classes.iconText}>
                  {t('company.how-it-works.candicates')}
                </p>
              </div>
              <div className={classes.iconContainer}>
                <img src={handshakeIcon} alt={t('company.how-it-works.agreement')} className={classes.icon} />
                <p className={classes.iconText}>
                  {t('company.how-it-works.agreement')}
                </p>
              </div>
              <div className={classes.iconContainer}>
                <img src={coinsIcon} alt={t('company.how-it-works.pay')} className={classes.icon} />
                <p className={classes.iconText}>
                  {t('company.how-it-works.pay')}
                </p>
              </div>
            </div>
            <Hidden mdUp>
              <div className={classes.signupButtonsContainer}>
                <Button
                  fullWidth={true}
                  color="primary"
                  onClick={() => scrollToView(signupSection)}
                >
                  {t('company.advantages.signup.buttons.register')}
                </Button>
              </div>
            </Hidden>
          </GridItem>
        </GridContainer>
        <GridContainer>
          <GridItem xs={12} md={10} className={classNames(classes.section, classes.noTopPadding)}>
            <div className={classes.decorationLineSectionFive} />
            <div className={classes.decorationLineHorizontalThree} />
            <div className={classes.decorationCircleHorizontalThree} />
            <div className={classes.diamondTwoContainer}>
              <img src={diamondTwoImage} className={classes.diamondImage} />
            </div>
            <div className={classes.lastSection} ref={signupSection}>
              {!messageSent
                ?<>
                <p className={classes.headline}>
                  {t('company.register-now')}
                </p>
                <p
                  className={classNames(classes.paragraph, classes.noPadding)}
                  dangerouslySetInnerHTML={{__html: sanitizeHtml(t('company.register-now-subtext'))}}
                />
                <p className={classNames(classes.paragraph, classes.noPadding)}>
                  {t('company.signup.text')}
                </p>
                <CustomInput
                  id="regular"
                  labelText={t('company.signup.email.placeholder')}
                  inputProps={{
                    value: inputValue,
                    onChange: handleChange
                  }}
                  success={validEmail(inputValue)}
                  error={displayError}
                  formControlProps={{
                    fullWidth: true
                  }}
                />
                <div className={classes.signupButtonsContainer}>
                  <Button onClick={handleClick} className={classes.signupButton} color="black">{t('company.signup.buttons.send')}</Button>
                </div>
              </>
              : <p className={classNames(classes.headline, classes.signupConfirmation)}>
                {t('company.signup.sent')}
              </p>
              }
            </div>
          </GridItem>
        </GridContainer>
      </div>
      <Footer />
    </div>
  );
}

export default CompanyPage;
